import styles from "./HistoryCorrections.module.css";

export interface HistoryCorrection {
  id: string;
  type: "stempling" | "pause" | "afvigelse" | "fravaer";
  handling: "oprettet" | "rettet" | "slettet";
  dato: string;
  foer: Record<string, unknown> | null;
  efter: Record<string, unknown> | null;
  sketeAt: string;
}

const datoFmt = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const tidFmt = new Intl.DateTimeFormat("da-DK", {
  hour: "2-digit",
  minute: "2-digit",
});

function tekst(payload: Record<string, unknown> | null, key: string): string | null {
  const value = payload?.[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function tal(payload: Record<string, unknown> | null, key: string): number | null {
  const value = payload?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function klokkeslaet(iso: string | null): string | null {
  if (iso === null) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : tidFmt.format(date);
}

function stemplingTekst(rettelse: HistoryCorrection): string {
  if (rettelse.handling === "slettet") {
    return tekst(rettelse.efter, "deleteReason") ?? "Registreringen blev slettet.";
  }
  const foerInd = klokkeslaet(tekst(rettelse.foer, "clockInAt"));
  const foerUd = klokkeslaet(tekst(rettelse.foer, "clockOutAt"));
  const efterInd = klokkeslaet(tekst(rettelse.efter, "clockInAt"));
  const efterUd = klokkeslaet(tekst(rettelse.efter, "clockOutAt"));
  const foer = foerInd === null ? null : `${foerInd}–${foerUd ?? "åben"}`;
  const efter = efterInd === null ? null : `${efterInd}–${efterUd ?? "åben"}`;
  if (foer !== null && efter !== null && foer !== efter) return `${foer} → ${efter}`;
  const note = tekst(rettelse.efter, "note");
  return note === null ? "Registreringen blev rettet." : `Ny note: ${note}`;
}

function pauseTekst(rettelse: HistoryCorrection): string {
  const foer = tal(rettelse.foer, "minutes");
  const efter = tal(rettelse.efter, "minutes");
  if (rettelse.handling === "slettet") return "Den manuelle pauserettelse blev fjernet.";
  if (foer !== null && efter !== null) return `${String(foer)} min → ${String(efter)} min`;
  if (efter !== null) return `Pausen blev sat til ${String(efter)} min.`;
  return "Pausen blev rettet.";
}

function afvigelseTekst(rettelse: HistoryCorrection): string {
  if (rettelse.handling === "slettet") return "Afvigelsen blev fjernet.";
  const type = tekst(rettelse.efter, "type");
  const minutes = tal(rettelse.efter, "minutes");
  const note = tekst(rettelse.efter, "note");
  const dele = [
    type === "fravaer" ? "Fravær/fri" : type === "ekstra" ? "Ekstra tid" : "Andet timetal",
    minutes === null || type === "fravaer" ? null : `${String(minutes)} min`,
    note,
  ].filter((value): value is string => value !== null);
  return dele.join(" · ");
}

function fravaerTekst(rettelse: HistoryCorrection): string {
  if (rettelse.handling === "slettet") return "Fraværet blev fjernet.";
  const type = tekst(rettelse.efter, "type");
  const note = tekst(rettelse.efter, "note");
  const label =
    type === "sygdom" ? "Sygdom" : type === "ferie" ? "Ferie" : "Fri";
  return [label, note].filter((value): value is string => value !== null).join(" · ");
}

function titel(rettelse: HistoryCorrection): string {
  const type =
    rettelse.type === "stempling"
      ? "Stempling"
      : rettelse.type === "pause"
        ? "Pause"
        : rettelse.type === "fravaer"
          ? "Fravær"
        : "Afvigelse";
  const handling =
    rettelse.handling === "slettet"
      ? "slettet"
      : rettelse.handling === "oprettet"
        ? "registreret"
        : "rettet";
  return `${type} ${handling}`;
}

function beskrivelse(rettelse: HistoryCorrection): string {
  if (rettelse.type === "stempling") return stemplingTekst(rettelse);
  if (rettelse.type === "pause") return pauseTekst(rettelse);
  if (rettelse.type === "fravaer") return fravaerTekst(rettelse);
  return afvigelseTekst(rettelse);
}

export function HistoryCorrections({
  rettelser,
  naesteCursor,
  henterFlere,
  sideFejl,
  onHentFlere,
  variant = "lys",
}: {
  rettelser: HistoryCorrection[];
  naesteCursor: string | null;
  henterFlere: boolean;
  sideFejl: boolean;
  onHentFlere: () => void;
  variant?: "lys" | "moerk";
}) {
  if (rettelser.length === 0) return null;
  return (
    <section
      className={`${styles.sektion} ${variant === "moerk" ? styles.moerk : ""}`}
      aria-label="Rettelser i perioden"
    >
      <h3 className={styles.titel}>Rettelser i perioden</h3>
      <ul className={styles.liste}>
        {rettelser.map((rettelse) => (
          <li
            key={rettelse.id}
            className={`${styles.kort} ${rettelse.handling === "slettet" ? styles.slettet : ""}`}
          >
            <div className={styles.toplinje}>
              <strong>{titel(rettelse)}</strong>
              <span>{datoFmt.format(new Date(`${rettelse.dato}T00:00:00`))}</span>
            </div>
            <p>{beskrivelse(rettelse)}</p>
          </li>
        ))}
      </ul>
      {sideFejl ? (
        <div className={styles.sideFejl} role="alert">
          <span>Flere rettelser kunne ikke hentes. De viste data er bevaret.</span>
          <button type="button" className={styles.flere} onClick={onHentFlere}>
            Prøv igen
          </button>
        </div>
      ) : naesteCursor !== null ? (
        <button
          type="button"
          className={styles.flere}
          disabled={henterFlere}
          onClick={onHentFlere}
        >
          {henterFlere ? "Henter…" : "Vis flere rettelser"}
        </button>
      ) : null}
    </section>
  );
}
